import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Import custom CSS for design system and mobile optimizations
import './styles/modern-theme.css';
import './styles/mobile-optimizations.css';
import { initializeSecurity, cleanupSecurity } from './lib/security/init';
// Removed old AuthProvider - using Cognito AuthProvider only
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AnalyticsProvider } from './providers/AnalyticsProvider';
import { useAnalytics } from './hooks/useAnalytics';
import { AuthGuard, SessionTimeout, SSOLoginForm, SSOCallback } from './components/auth';
import LoginForm from './components/auth/LoginForm';
import LoginPage from './components/auth/LoginPage';
import { Sidebar } from './components/Layout/Sidebar';
import LandingPage from './pages/LandingPage';
import {
  createLazyComponent,
  withPageLazyLoading,
  PageLoadingSpinner,
  ComponentPreloader
} from './lib/performance/lazy-loading';

// Lazy load all dashboard components
// Named exports need .then(m => ({ default: m.ComponentName }))
// Default exports can use the module directly

// Named exports (no default export) - using React.lazy directly for proper typing
const OverviewDashboard = React.lazy(() =>
  import('./components/Overview/OverviewDashboard').then(m => ({ default: m.OverviewDashboard }))
);

const MeasureDashboard = React.lazy(() =>
  import('./components/Measure/MeasureDashboard').then(m => ({ default: m.MeasureDashboard }))
);

const OptimizeDashboard = React.lazy(() =>
  import('./components/Optimize/OptimizeDashboard').then(m => ({ default: m.OptimizeDashboard }))
);

const DeployDashboard = React.lazy(() =>
  import('./components/Deploy/DeployDashboard').then(m => ({ default: m.DeployDashboard }))
);

const ProveImpactDashboard = React.lazy(() =>
  import('./components/ProveImpact/ProveImpactDashboard').then(m => ({ default: m.ProveImpactDashboard }))
);

const TopologyDashboard = React.lazy(() =>
  import('./components/Topology/TopologyDashboard').then(m => ({ default: m.TopologyDashboard }))
);

// Default exports (have export default)
const UserManagementDashboard = createLazyComponent(
  () => import('./components/admin/UserManagementDashboard'),
  { componentName: 'UserManagementDashboard' }
);

const SSOConfigurationPanel = createLazyComponent(
  () => import('./components/admin/SSOConfigurationPanel'),
  { componentName: 'SSOConfigurationPanel' }
);

const SecurityDashboard = createLazyComponent(
  () => import('./components/security/SecurityDashboard'),
  { componentName: 'SecurityDashboard' }
);

const LakeFSDashboard = createLazyComponent(
  () => import('./components/lakefs/LakeFSDashboard'),
  { componentName: 'LakeFSDashboard' }
);

const PerformanceDashboard = createLazyComponent(
  () => import('./components/performance/PerformanceDashboard'),
  { componentName: 'PerformanceDashboard' }
);

const InvitationAcceptPage = createLazyComponent(
  () => import('./components/admin/InvitationAcceptPage'),
  { componentName: 'InvitationAcceptPage' }
);

type View = 'overview' | 'measure' | 'optimize' | 'deploy' | 'prove-impact';

// Dashboard Layout Component (for authenticated users)
function DashboardLayout() {
  const [currentView, setCurrentView] = useState<View>('overview');
  const { user } = useAuth();
  const location = useLocation();
  const analytics = useAnalytics();

  // Set current view based on pathname and track dashboard views
  useEffect(() => {
    const path = location.pathname;
    let newView: View = 'overview';

    if (path.includes('/dashboard/measure')) newView = 'measure';
    else if (path.includes('/dashboard/optimize')) newView = 'optimize';
    else if (path.includes('/dashboard/deploy')) newView = 'deploy';
    else if (path.includes('/dashboard/prove-impact')) newView = 'prove-impact';

    // Track dashboard view change if the view actually changed
    if (newView !== currentView) {
      analytics.navigation.dashboardSwitch(currentView, newView);
    }

    setCurrentView(newView);
  }, [location.pathname, currentView, analytics]);

  // Preload components based on current view to improve performance
  useEffect(() => {
    const preloadComponents = () => {
      // Preload likely next components based on user flow
      if (currentView === 'overview') {
        // Users often go from overview to measure
        ComponentPreloader.preload(MeasureDashboard, 'MeasureDashboard');
      } else if (currentView === 'measure') {
        // From measure, users often go to optimize
        ComponentPreloader.preload(OptimizeDashboard, 'OptimizeDashboard');
      } else if (currentView === 'optimize') {
        // From optimize, users often go to deploy
        ComponentPreloader.preload(DeployDashboard, 'DeployDashboard');
      } else if (currentView === 'deploy') {
        // From deploy, users often want to prove impact
        ComponentPreloader.preload(ProveImpactDashboard, 'ProveImpactDashboard');
      }

      // Always preload admin components for admin users
      if (user?.role === 'super_admin' || user?.role === 'org_admin') {
        ComponentPreloader.preloadMultiple([
          { component: UserManagementDashboard, id: 'UserManagementDashboard' },
          { component: SecurityDashboard, id: 'SecurityDashboard' },
          { component: PerformanceDashboard, id: 'PerformanceDashboard' }
        ]);
      }
    };

    // Preload after a short delay to not interfere with current page loading
    const timeoutId = setTimeout(preloadComponents, 1000);
    return () => clearTimeout(timeoutId);
  }, [currentView, user?.role]);

  // Set up API client with auth token when user is authenticated
  useEffect(() => {
    if (user) {
      // In a real implementation, you would get the access token from the auth context
      // For now, we'll set up the API client to be ready for authenticated requests
      // The actual token will be injected by the HTTP client interceptors
    }
  }, [user]);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        currentView={currentView}
        onViewChange={setCurrentView}
        user={user}
      />
      <main className="flex-1 overflow-auto">
        <Suspense fallback={<PageLoadingSpinner />}>
          <Routes>
            <Route path="overview" element={<OverviewDashboard />} />
            <Route path="measure" element={<MeasureDashboard />} />
            <Route path="optimize" element={<OptimizeDashboard />} />
            <Route path="deploy" element={<DeployDashboard />} />
            <Route path="prove-impact" element={<ProveImpactDashboard />} />
            <Route path="topology" element={<TopologyDashboard />} />
            <Route path="admin/users" element={<UserManagementDashboard />} />
            <Route path="admin/sso" element={<SSOConfigurationPanel />} />
            <Route path="admin/security" element={<SecurityDashboard />} />
            <Route path="admin/performance" element={<PerformanceDashboard />} />
            <Route path="lakefs" element={<LakeFSDashboard />} />
            <Route path="" element={<Navigate to="overview" replace />} />
          </Routes>
        </Suspense>
      </main>

      {/* Session timeout management */}
      <SessionTimeout
        warningTimeMinutes={5}
        inactivityMinutes={30}
        enableInactivityTimeout={true}
        onTimeout={() => {
          console.log('Session timed out');
        }}
        onWarning={() => {
          console.log('Session expiring soon');
        }}
      />
    </div>
  );
}


// App component with proper AuthProvider wrapping
function AppWithRouter() {
  return (
    <AnalyticsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public landing page */}
            <Route path="/" element={<LandingPage />} />

            {/* Authentication routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/login" element={<SSOLoginForm />} />
            <Route path="/auth/sso/callback/:provider" element={<SSOCallback />} />
            <Route path="/auth/accept-invitation" element={<InvitationAcceptPage />} />

            {/* Protected dashboard routes */}
            <Route path="/dashboard/*" element={
              <AuthGuard>
                <DashboardLayout />
              </AuthGuard>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </AnalyticsProvider>
  );
}

// Root app component with security initialization
export default function App() {
  useEffect(() => {
    // Initialize security services when app starts
    initializeSecurity();

    // Cleanup on unmount
    return () => {
      cleanupSecurity();
    };
  }, []);

  return <AppWithRouter />;
}